import React, { useCallback, useEffect, useRef, useState } from 'react';
import MessageDialog, {
  type MessageDialogOpen,
  type MessageDialogTone,
} from '../components/MessageDialog';

export type AppAlertOptions = {
  title?: string;
  tone?: MessageDialogTone;
};

export type AppConfirmOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type AlertFn = (message: string, options?: AppAlertOptions) => void;
type ConfirmFn = (message: string, options?: AppConfirmOptions) => Promise<boolean>;

let alertImpl: AlertFn | null = null;
let confirmImpl: ConfirmFn | null = null;

function inferTone(message: string): MessageDialogTone {
  const m = message.toLowerCase();
  // Check success first — publish text can include “required to acknowledge”.
  if (/(success|saved|sent|updated|deleted|created|completed|published|migrated|will receive)/.test(m)) {
    return 'success';
  }
  if (/(fail|error|could not|unable|invalid|denied|blocked|\bis required\b)/.test(m)) {
    return 'error';
  }
  return 'info';
}

function inferAlertTitle(tone: MessageDialogTone): string {
  if (tone === 'error') return 'Something went wrong';
  if (tone === 'success') return 'Success';
  return 'Notice';
}

function inferConfirmTitle(message: string, danger?: boolean): string {
  if (danger) return 'Please confirm';
  if (/delete|remove|archive|cancel|reject|deactivate/i.test(message)) return 'Please confirm';
  return 'Confirm';
}

/** Show a modal alert (replaces window.alert). */
export function appAlert(message: string, options?: AppAlertOptions): void {
  const text = String(message ?? '');
  if (alertImpl) {
    alertImpl(text, options);
    return;
  }
  // Fallback before provider mounts
  // eslint-disable-next-line no-alert
  window.alert(text);
}

/** Show a modal confirm and resolve true/false (replaces window.confirm). */
export async function appConfirm(message: string, options?: AppConfirmOptions): Promise<boolean> {
  const text = String(message ?? '');
  if (confirmImpl) return confirmImpl(text, options);
  // eslint-disable-next-line no-alert
  return window.confirm(text);
}

export const SystemDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MessageDialogOpen>(null);
  const confirmResolver = useRef<((value: boolean) => void) | null>(null);
  const settled = useRef(false);

  const closeConfirm = useCallback((value: boolean) => {
    if (settled.current) return;
    settled.current = true;
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    setState(null);
    resolve?.(value);
  }, []);

  const showAlert = useCallback<AlertFn>((message, options) => {
    const tone = options?.tone ?? inferTone(message);
    setState({
      variant: 'alert',
      title: options?.title ?? inferAlertTitle(tone),
      message,
      tone,
    });
  }, []);

  const showConfirm = useCallback<ConfirmFn>((message, options) => {
    settled.current = false;
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve;
      setState({
        variant: 'confirm',
        title: options?.title ?? inferConfirmTitle(message, options?.danger),
        message,
        tone: options?.danger ? 'danger' : 'default',
        confirmLabel: options?.confirmLabel,
        cancelLabel: options?.cancelLabel,
        onConfirm: () => closeConfirm(true),
      });
    });
  }, [closeConfirm]);

  useEffect(() => {
    alertImpl = showAlert;
    confirmImpl = showConfirm;

    const previousAlert = window.alert.bind(window);
    // Route native alerts through the modal. Confirms must use await appConfirm(...) — do not
    // override window.confirm (it is sync and cannot safely return a Promise as boolean).
    window.alert = ((message?: any) => {
      showAlert(message == null ? '' : String(message));
    }) as typeof window.alert;

    return () => {
      alertImpl = null;
      confirmImpl = null;
      window.alert = previousAlert;
    };
  }, [showAlert, showConfirm]);

  const onClose = useCallback(() => {
    if (state?.variant === 'confirm') {
      // Confirm button already resolved via onConfirm → closeConfirm(true); settled guards cancel.
      closeConfirm(false);
      return;
    }
    setState(null);
  }, [state, closeConfirm]);

  return (
    <>
      {children}
      <MessageDialog state={state} onClose={onClose} />
    </>
  );
};
